import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { User } from '@core/models/user.model';
import { AuthService } from '@core/services/auth.service';
import { WorkspaceService } from '@core/services/workspace.service';
import { map, Observable } from 'rxjs';

/**
 * Ensures the workspace shell only renders when a workspace is active. Handles
 * the entry rules: super admins go to the platform; single-workspace users are
 * auto-selected; everyone else is sent to selection or creation.
 *
 * Must await ensureSession when a JWT exists but the in-memory user is not
 * hydrated yet — otherwise a parallel race with authGuard can wrongly send
 * users to /login (common when opening "/" while on workspace-creation).
 */
export const workspaceGuard: CanActivateFn = ():
  | boolean
  | UrlTree
  | Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const ws = inject(WorkspaceService);
  const router = inject(Router);

  auth.purgeLegacyDemoStorage();
  if (!auth.hasToken()) {
    auth.logout();
    ws.clear();
    return router.createUrlTree(['/login']);
  }

  const resolve = (user: User | null): boolean | UrlTree => {
    if (!user) {
      auth.logout();
      ws.clear();
      return router.createUrlTree(['/login']);
    }
    if (user.platformRole === 'super_admin') {
      return router.createUrlTree(['/platform/dashboard']);
    }

    if (ws.currentWorkspace()) {
      // Drop stale selection if membership list no longer includes it.
      const stillMember = user.workspaces.some(
        (w) => w.id === ws.currentWorkspace()?.id,
      );
      if (stillMember) return true;
      ws.clear();
    }

    // On a full page reload the WorkspaceService constructor runs before the
    // session user is hydrated, so its restore() is a no-op. Retry here now
    // that memberships are known, so multi-workspace users keep their
    // selection across refreshes instead of landing on /select-workspace.
    if (ws.restoreFromStorage()) {
      return true;
    }

    if (user.workspaces.length === 1) {
      ws.select(user.workspaces[0]);
      return true;
    }
    if (user.workspaces.length === 0) {
      return router.createUrlTree(['/workspace-creation']);
    }
    return router.createUrlTree(['/select-workspace']);
  };

  const existing = auth.currentUser();
  if (existing) return resolve(existing);

  return auth.ensureSession().pipe(map(resolve));
};
